import { useLazyQuery, useMutation } from '@apollo/client/react';
import { formatISO } from 'date-fns';
import { useCallback, useEffect, useRef } from 'react';

import {
  FetchMedixsReceptionDocument,
  GetPatientProfileMedixsReceptionAppointmentDocument,
  IdentifyMedixsReceptionDocument,
  MedixsReceptionAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { useMedixsSetting } from '~/hooks/use-medixs-setting';

export const useFetchAndIdentifyMedixsReception = (appointmentId: string | null | undefined) => {
  const { enabledMedixs } = useMedixsSetting();
  const failedAppointmentIdsRef = useRef<Set<string>>(new Set());

  const [fetchMedixsReception, { loading: isFetching, error: fetchMedixsReceptionError }] =
    useMutation(FetchMedixsReceptionDocument);
  const [
    identifyMedixsReception,
    {
      data: identifyMedixsReceptionData,
      loading: isIdentifying,
      error: identifyMedixsReceptionError,
    },
  ] = useMutation(IdentifyMedixsReceptionDocument, {
    refetchQueries: [
      {
        query: GetPatientProfileMedixsReceptionAppointmentDocument,
        variables: { appointmentId },
      },
    ],
  });
  const fetchAndIdentifyMedixsReception = useCallback(
    (appointment: MedixsReceptionAppointmentFragment) => {
      if (isFetching || isIdentifying) return;
      if (failedAppointmentIdsRef.current.has(appointment.id)) return;
      const medixsPatient = appointment.patient?.medixsPatient;

      // appointmentが完了・キャンセル状態でなく、
      // かつ、medixs_patient連携済みで、
      // かつ、medixs_reception連携前のときのみ、自動連携処理を実行する
      if (
        appointment.status !== 'finished' &&
        appointment.status !== 'cancelled' &&
        appointment.status !== 'noshow' &&
        enabledMedixs &&
        appointment.createdAt &&
        medixsPatient &&
        !appointment?.medixsReception
      ) {
        console.log('自動連携開始');
        fetchMedixsReception({
          variables: {
            input: {
              date: formatISO(new Date(appointment.createdAt), { representation: 'date' }),
              // レセコン入力が予約作成より前でも拾えるように、その日の頭から検索する
              startAt: '00:00',
              endAt: '23:59',
            },
          },
        })
          .then(() => {
            return identifyMedixsReception({
              variables: {
                input: {
                  appointmentId: appointment.id,
                },
              },
            });
          })
          .catch(() => {
            failedAppointmentIdsRef.current.add(appointment.id);
          });
      }
    },
    [enabledMedixs, fetchMedixsReception, identifyMedixsReception, isFetching, isIdentifying],
  );
  const [getAppointment, { data: appointmentData }] = useLazyQuery(
    GetPatientProfileMedixsReceptionAppointmentDocument,
  );

  useEffect(() => {
    if (appointmentId && enabledMedixs) {
      getAppointment({
        variables: {
          appointmentId,
        },
      });
    }
  }, [appointmentId, enabledMedixs, getAppointment]);

  useEffect(() => {
    if (appointmentData) {
      const appointment = getNode(appointmentData, 'Appointment');

      if (appointment) {
        fetchAndIdentifyMedixsReception(appointment);
      }
    }
  }, [appointmentData, fetchAndIdentifyMedixsReception]);

  useEffect(() => {
    if (fetchMedixsReceptionError) {
      console.log(fetchMedixsReceptionError);
    }
  }, [fetchMedixsReceptionError]);

  useEffect(() => {
    if (identifyMedixsReceptionError) {
      console.log('自動連携NG');
    }
  }, [identifyMedixsReceptionError]);

  useEffect(() => {
    if (identifyMedixsReceptionData) {
      console.log('自動連携OK');
    }
  }, [identifyMedixsReceptionData]);
};
